import { useRef } from 'react'
import Portrait from '../Portrait'
import { displayPosition, positionBorder, positionFill } from './positions'
import { useFlightToCell } from './useFlightToCell'
import type { Verdict } from './verdict'
import type { PlayerSummary, TeamState } from '../../types/protocol'

// The ~4.2s celebration over a fresh pick. This is the one place the board
// spells the player's name out in full — the grid cell abbreviates because it
// has 267x113 CSS px, the overlay has the whole screen.
//
// It is also the board's only *full* portrait. The grid cells are fixed at two
// lines in ~267x113 CSS px, so a headshot there has to be a narrow crop that
// gives the name back most of its width. Here there is an entire screen, so the
// photo can be the subject rather than a concession.
//
// The sequence is a set of --animate-flash-* tokens, one per element: a signal
// bar wipes across and splits open, the card reveals through it, and the
// portrait, team, name and meta then arrive on separate beats. Every element
// carries exactly one animation, because a second entry in an animation list
// wins the properties it shares with the first. It ends with the card flying
// into the grid cell it belongs to — see useFlightToCell. This component only
// mounts and unmounts around all that, so a pick landing mid-flash restarts
// cleanly via the key on the wrapper.

// The two beat sets. Whole class names, never interpolated — Tailwind scans
// source text for candidates and would never see `animate-flash-${x}`. Same rule
// as POSITION_FILL in positions.ts.
//
// The fast set is the same keyframes at the same durations, 650ms earlier,
// because there is no drum roll to wait out. Skipping the bar is not the same as
// playing the reveal faster: every beat keeps its spacing relative to the others.
const FULL_BEATS = {
  card: 'animate-flash-card',
  portrait: 'animate-flash-portrait',
  team: 'animate-flash-team',
  name: 'animate-flash-name',
  meta: 'animate-flash-meta',
  verdict: 'animate-flash-verdict',
  scrim: 'animate-flash-scrim-out',
  flare: 'animate-flash-flare',
} as const

const FAST_BEATS = {
  card: 'animate-flash-card-fast',
  portrait: 'animate-flash-portrait-fast',
  team: 'animate-flash-team-fast',
  name: 'animate-flash-name-fast',
  meta: 'animate-flash-meta-fast',
  verdict: 'animate-flash-verdict-fast',
  scrim: 'animate-flash-scrim-out-fast',
  flare: 'animate-flash-flare-fast',
} as const

export type FlashBeats = typeof FULL_BEATS | typeof FAST_BEATS

// 3900 leaves a ~1.3s readable hold after the verdict lands at 2610, and puts
// the card down at 4500 — inside FULL_MS (4550) with a frame to spare. On the
// fast path the whole flash *is* 2500, so the flight has to start earlier or it
// would begin exactly as the overlay unmounts and never be seen: 1850 lands it
// at 2450, leaving a ~650ms hold. That is short, and it is the right trade —
// when picks are landing this fast the flight is there to show where the card
// went, and the hold has nothing to add.
const FLIGHT_DELAY_MS = 3900
const FLIGHT_DELAY_FAST_MS = 1850

interface Props {
  overall: number
  round: number
  slot: number
  player: PlayerSummary | null
  team: TeamState | null
  /** null for the great majority of picks — render nothing at all. */
  verdict: Verdict | null
  /** False during back-to-back picks — the drum roll is skipped. */
  preroll: boolean
}

export default function PickFlash({
  overall,
  round,
  slot,
  player,
  team,
  verdict,
  preroll,
}: Props) {
  const position = displayPosition(player?.position)
  const beats = preroll ? FULL_BEATS : FAST_BEATS
  const cardRef = useRef<HTMLDivElement>(null)
  const cellRect = useFlightToCell(
    cardRef,
    overall,
    preroll ? FLIGHT_DELAY_MS : FLIGHT_DELAY_FAST_MS,
  )

  return (
    // The wrapper carries no animation of its own now. With reduced motion on,
    // or when the destination cell is missing, the card therefore has no exit
    // and simply disappears on unmount — deliberate, because both are already
    // the degraded path and a conditional CSS exit would be a second mechanism
    // racing the flight for the card's opacity.
    <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center">
      {/* Was bg-ink/85 on the wrapper. It is its own element now because the card
          no longer leaves with it — the card flies out on its own timeline, and the
          scrim has to be gone before it arrives or it lands on a dimmed board. */}
      <div className={`${beats.scrim} absolute inset-0 bg-ink/85`} />

      <div
        ref={cardRef}
        className={`${positionFill(player?.position)} ${beats.card} text-pos-ink flex max-w-[86vw] items-center gap-[3vw] rounded-[2vh] px-[4vw] py-[4vh] shadow-[inset_0_0.3vh_0_rgb(255_255_255/0.22),0_4vh_10vh_-3vh_rgb(0_0_0/0.8)]`}
      >
        {player && (
          <Portrait
            fullName={player.full_name}
            position={player.position}
            nflTeam={player.nfl_team}
            src={player.portrait_url}
            jerseyNumber={player.jersey_number}
            size="flash"
            // The plate is normally a dark surface; on the position fill it
            // needs to read as a window cut into the card instead.
            className={`${beats.portrait} bg-pos-ink/25 shadow-[inset_0_0_0_0.3vh_rgb(0_0_0/0.22)]`}
          />
        )}

        <div className="flex min-w-0 flex-col gap-[1vh]">
          {/* The team name is the shrinkable half and the badge is fixed, so a
              long team name truncates instead of pushing the badge off the card.
              Without min-w-0 a nowrap flex child refuses to shrink below its
              text and overflows the card's max-w-[86vw] instead.

              The team's beat moved off the <p> and onto this row, so the name
              and the badge arrive on one beat; the badge then stamps down on a
              later one of its own. */}
          <div className={`${beats.team} flex min-w-0 items-center gap-[1.6vw]`}>
            <p className="text-board-hero font-display min-w-0 truncate leading-none font-semibold tracking-[0.12em] uppercase opacity-75">
              {team?.name ?? 'Pick'}
            </p>
            {verdict && <VerdictBadge verdict={verdict} beat={beats.verdict} />}
          </div>
          <p className={`${beats.name} text-board-flash-name font-display leading-[0.9] font-bold`}>
            {player?.full_name ?? 'Pick made'}
          </p>
          <p
            className={`${beats.meta} text-board-flash-meta font-mono font-semibold tabular-nums opacity-85`}
          >
            {position} · {player?.nfl_team ?? 'FA'}
            {/* player is null until the pool resolves the pick — the flash
                still renders "Pick made" behind it, so this has to tolerate
                both a missing player and a missing bye. */}
            {player?.bye_week != null && ` · BYE ${player.bye_week}`} · R{round}.{slot} · #
            {overall}
          </p>
        </div>
      </div>

      {/* The drum roll, when the board has been quiet long enough to have earned
          it. Painted over the card it is revealing: the wipe covers the screen,
          then splits along its centre line and the card is already there behind
          it. So it must come *after* the card in the DOM — above it, the card sits
          behind an opaque bar for the whole flash. Sized off the viewport like
          everything else on this page, and aria-hidden because it is a transition,
          not information — the board has no assistive-tech user and the name is
          spoken by nothing here anyway.

          This is the only beat the fast path drops. The card, portrait, team, name
          and meta keep their exact delays in both modes: skipping the bar is not
          the same as playing the same reveal faster. */}
      {preroll && (
        <div
          aria-hidden="true"
          className="animate-flash-preroll text-board-flash-meta absolute inset-x-0 top-1/2 flex h-[9vh] -translate-y-1/2 items-center justify-center bg-signal font-mono font-semibold tracking-[0.42em] text-ink uppercase"
        >
          The pick is in
        </div>
      )}

      {/* The ring the cell wears as the card lands. Drawn here, in viewport
          coordinates, rather than as a class on the cell — PickCell's className is
          fully controlled by React and would wipe it on the next pick's re-render.
          Inset slightly outside the cell so it reads as a ring around it.

          Last child, so nothing in the overlay paints over it. It exists from
          mount but its keyframe holds opacity 0 through the delay — 3050ms on the
          full sequence, 2400ms on the fast path, each landing ~50ms before the
          card touches down. */}
      {cellRect && (
        <span
          aria-hidden="true"
          className={`${positionBorder(player?.position)} ${beats.flare} absolute rounded-[0.9vh] border-[0.4vh]`}
          style={{
            left: cellRect.left - 4,
            top: cellRect.top - 4,
            width: cellRect.width + 8,
            height: cellRect.height + 8,
          }}
        />
      )}
    </div>
  )
}

// Deliberately no new hue. Every accent in the system is spoken for — signal is
// identity, turn means "it's you" and nothing else, warn and crit belong to the
// clock — and this plate sits on six different position fills, so a colour that
// read well on the blue WR card would fight the red QB one.
//
// The two states are told apart by *polarity* instead: a steal is a near-black
// plate that reads as a hole cut into the card, a reach is a near-white one that
// reads as a label stuck on it. Both are solid, so both stay legible across a
// room on all six fills.
//
// The reach was originally an outline at 0.8 opacity, on the theory that a steal
// should shout louder. It did not survive contact with the TV: a dimmed dark
// outline on a mid-tone position fill is the one combination in this system with
// no contrast to spare. Legibility wins — a badge nobody can read carries no
// hierarchy at all.
function VerdictBadge({ verdict, beat }: { verdict: Verdict; beat: string }) {
  const skin =
    verdict.kind === 'steal' ? 'bg-pos-ink text-ink-hi' : 'bg-ink-hi text-pos-ink'

  return (
    <span
      className={`${skin} ${beat} text-board-flash-meta flex flex-none items-baseline gap-[0.9vw] rounded-[0.9vh] px-[1.6vh] py-[0.9vh] font-mono leading-none font-semibold tracking-[0.16em] whitespace-nowrap uppercase`}
    >
      <b className="font-semibold">{verdict.word}</b>
      <i className="font-medium not-italic opacity-70 tabular-nums">{verdict.detail}</i>
    </span>
  )
}
